import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  createCrmJobSetting,
  CrmJobStatusDTO,
  CrmJobSettingsDTO,
  getCrmJobSettingsByWorkspaceId,
  getCrmJobStatusList,
  updateCrmJobSettings,
} from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import * as yup from 'yup';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { periodOptions } from '../helper/crmJobHelper';

const defaultCrmJobData: CrmJobSettingsDTO = {
  coolingPeriod: 0,
  coolingPeriodFrequency: 'DAYS',
  reviewContactCount: 0,
  workspaceId: null,
  closedJobStatusId: '',
};

const getValidationSchema = () => {
  return yup.object().shape({
    coolingPeriod: yup.number().required('Cooling period is required'),
    coolingPeriodFrequency: yup.string().required('Frequency is required'),
    reviewContactCount: yup
      .number()
      .required('Review contact count is required')
      .min(1, 'Must be at least 1'),
    closedJobStatusId: yup.string().required('Closed job status is required'),
  });
};

const CrmJobSettings = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [statusList, setStatusList] = useState<WsSelectObj[]>([]);

  const methods = useForm<CrmJobSettingsDTO>({
    resolver: yupResolver(getValidationSchema()),
    defaultValues: defaultCrmJobData,
  });

  const fetchCrmJobStatusList = async () => {
    const response = await getCrmJobStatusList();
    if (response.data) {
      const crmJobStatusList: CrmJobStatusDTO[] = response.data;
      const statusList = crmJobStatusList.map((status) => ({
        label: status.statusName,
        value: status.id,
      }));
      setStatusList(statusList);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (workspaceContext?.id) {
          const response = await getCrmJobSettingsByWorkspaceId(workspaceContext.id);
          if (response.data) {
            methods.reset(response.data);
          }
        }
      } catch (error) {
        console.error('Error fetching CRM job settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCrmJobStatusList();
    fetchSettings();
  }, [workspaceContext?.id]);

  const onSubmit = async (data: CrmJobSettingsDTO) => {
    try {
      const response = data.id ? await updateCrmJobSettings(data) : await createCrmJobSetting(data);
      if (response?.data) {
        methods.reset(response.data);
        setNotification(success('Settings saved successfully'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setNotification(failed('Error saving settings'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsPageLayout
      title="CRM Job Settings"
      indexOptions={[{ label: 'CRM Job Settings', url: '/crm-job-settings' }]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.CRM,
        Pages['CRM_JOB_SETTINGS']
      )}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={4} p={2}>
            <Grid item xs={12}>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    Cooling Period
                  </WsTypography>
                </Grid>
                <Grid item>
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                    Specify the cooling period for when a contact can be reached again.
                  </WsTypography>
                </Grid>
                <Grid item>
                  <Grid container direction="row" spacing={2} alignItems="center" mt={1}>
                    <Grid item>
                      <ControlledTextInput
                        name="coolingPeriod"
                        type="number"
                        fullWidth
                        variant="outlined"
                        showMandatorySymbol
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <WsSelect
                        name="coolingPeriodFrequency"
                        selectArray={periodOptions}
                        showMandatorySymbol
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid
                container
                direction="column"
                spacing={1}
                borderTop={1}
                borderColor={WsColors.BorderGrey}
              >
                <Grid item>
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    Review Contact Count
                  </WsTypography>
                </Grid>
                <Grid item>
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                    Select maximum number of contacts you can reach from the eligible list.
                  </WsTypography>
                </Grid>
                <Grid item>
                  <ControlledTextInput
                    name="reviewContactCount"
                    type="number"
                    variant="outlined"
                    showMandatorySymbol
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid
                container
                direction="column"
                spacing={1}
                borderTop={1}
                borderColor={WsColors.BorderGrey}
              >
                <Grid item>
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    Convert to Lead Status
                  </WsTypography>
                </Grid>
                <Grid item>
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                    Select status for closing a job.
                  </WsTypography>
                </Grid>
                <Grid item xs={4}>
                  <Grid container direction="row" spacing={2} alignItems="center" mt={1}>
                    <Grid item xs={4}>
                      <WsSelect
                        key="status"
                        name="closedJobStatusId"
                        selectArray={statusList}
                        variant="outlined"
                        showMandatorySymbol
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container direction="row" spacing={2} justifyContent="flex-end">
                <Grid item>
                  <WsButton
                    variant="outlined"
                    onClick={() => navigate('/app-settings/crm')}
                    disabled={isLoading}
                  >
                    Discard
                  </WsButton>
                </Grid>
                <Grid item>
                  <WsButton variant="contained" type="submit" disabled={isLoading}>
                    Save
                  </WsButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </SettingsPageLayout>
  );
};

export default CrmJobSettings;
